/**
 * Общая разметка секции runtime-карточки: заголовок и опционально сворачивание.
 * ОИ может задавать цветовую обёртку и классы триггера через sectionColorPolicies;
 * РД — нейтральный вид по умолчанию.
 */

import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  getCardSectionOpen,
  setCardSectionOpen,
} from "@/lib/card-section-open-session";

/** Сетка полей по умолчанию для runtime (РД и совместимые экраны). */
export const CARD_RUNTIME_FIELD_GRID_CLASS = "grid gap-3 md:grid-cols-2";

/** Триггер сворачиваемой секции без цветовой политики ОИ (нейтральный каркас). */
export const CARD_RUNTIME_DEFAULT_COLLAPSIBLE_TRIGGER_CLASS =
  "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm font-medium hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const CHEVRON_CLASS = "h-4 w-4 shrink-0 transition-transform duration-200";

export interface CardRuntimeSectionLayoutProps {
  title: string;
  isCollapsible: boolean;
  defaultOpen?: boolean;
  /** Для persist open-state в сессии вкладки. */
  objectId?: number | null;
  /** Ключ секции внутри objectId (id секции или «orphan»). */
  sectionPersistKey?: string;
  /** Обёртка секции (рамка/фон ОИ — sectionColorWrapperClass и т.п.). */
  sectionRootClassName?: string;
  collapsibleTriggerClassName?: string;
  collapsibleContentClassName?: string;
  /** Заголовок, если секция не сворачивается. */
  staticTitleClassName?: string;
  /** Дополнительные классы корня в статическом режиме (напр. space-y-2 у РД). */
  staticRootExtraClassName?: string;
  /** Иконка раскрытия: слева (РД) или справа от заголовка (ОИ). */
  chevronPosition?: "start" | "end";
  /** Раскрыть секцию, когда в ней есть ошибка валидации. */
  forceOpen?: boolean;
  children: ReactNode;
}

export function CardRuntimeSectionLayout({
  title,
  isCollapsible,
  defaultOpen = true,
  objectId = null,
  sectionPersistKey,
  sectionRootClassName,
  collapsibleTriggerClassName,
  collapsibleContentClassName = "pt-3",
  staticTitleClassName = "text-sm font-medium",
  staticRootExtraClassName,
  chevronPosition = "start",
  forceOpen = false,
  children,
}: CardRuntimeSectionLayoutProps) {
  const persistKey = sectionPersistKey ?? title;
  const [open, setOpen] = useState(() =>
    getCardSectionOpen(objectId, persistKey, defaultOpen),
  );

  useEffect(() => {
    if (!forceOpen || !isCollapsible) return;
    setOpen(true);
    setCardSectionOpen(objectId, persistKey, true);
  }, [forceOpen, isCollapsible, objectId, persistKey]);

  if (!isCollapsible) {
    return (
      <div className={cn(staticRootExtraClassName, sectionRootClassName)}>
        <h4 className={staticTitleClassName}>{title}</h4>
        {children}
      </div>
    );
  }

  const triggerClasses = cn(
    collapsibleTriggerClassName ?? CARD_RUNTIME_DEFAULT_COLLAPSIBLE_TRIGGER_CLASS,
    "[&[data-state=open]>svg]:rotate-180",
  );

  const chevron = <ChevronDown className={CHEVRON_CLASS} aria-hidden />;

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    setCardSectionOpen(objectId, persistKey, next);
  };

  const collapsible = (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger
        type="button"
        className={triggerClasses}
        aria-expanded={open}
        aria-label={`${open ? "Свернуть" : "Развернуть"} секцию «${title}»`}
      >
        {chevronPosition === "start" ? (
          <>
            {chevron}
            <span className="min-w-0 flex-1">{title}</span>
          </>
        ) : (
          <>
            <span className="min-w-0 flex-1">{title}</span>
            {chevron}
          </>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className={collapsibleContentClassName}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  );

  if (sectionRootClassName) {
    return <div className={sectionRootClassName}>{collapsible}</div>;
  }

  return collapsible;
}
